import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Card } from '../ui/card';
import { 
  X, Send, User, Bot, Loader2, Clock, MessageCircle, 
  Headphones, ArrowLeft, Users, UserCheck, Ticket, ChevronRight, AlertCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

export const LiveChatWidget = ({ isOpen, onClose, onBackToAmie }) => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [queuePosition, setQueuePosition] = useState(0);
  
  // New states for enhanced flow
  const [step, setStep] = useState('initial'); // initial, groups, agents, no_agents, queue, active, chatback_offer, chatback_confirmed
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [agentsInGroup, setAgentsInGroup] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [initialMessage, setInitialMessage] = useState('');
  const [agentTyping, setAgentTyping] = useState(false);
  const lastTypingSentRef = useRef(0);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  
  const isITILUser = user?.role === 'itil_staff' || user?.role === 'admin' || user?.role === 'super_admin';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for existing session on mount
  useEffect(() => {
    if (isOpen && !session) {
      checkExistingSession();
    }
  }, [isOpen]);

  const checkExistingSession = async () => {
    try {
      const sessions = await api.getChatSessions();
      const activeSession = sessions.find(s => s.status === 'active' || s.status === 'queued');
      if (activeSession) {
        setSession(activeSession);
        const chatData = await api.getChatSession(activeSession.id);
        setMessages(chatData.messages);
        setQueuePosition(activeSession.queue_position);
        setStep(activeSession.status === 'active' ? 'active' : 'queue');
      } else {
        // No existing session - initialize the flow
        initializeFlow();
      }
    } catch (error) {
      console.error('Error checking sessions:', error);
      initializeFlow();
    }
  };

  // Initialize flow based on user role
  const initializeFlow = async () => {
    setLoading(true);
    try {
      if (isITILUser) {
        // ITIL staff: Load assignment groups with online agents
        const groupsData = await api.getGroupsWithAgents();
        setGroups(groupsData);
        setStep('groups');
      } else {
        // End user: Check agent availability
        const availData = await api.checkAgentAvailability();
        setAvailability(availData);
        if (availData.agents_available) {
          setStep('initial');
        } else {
          setStep('no_agents');
        }
      }
    } catch (error) {
      console.error('Error initializing flow:', error);
      setStep('initial'); // Fallback to basic flow
    } finally {
      setLoading(false);
    }
  };

  // ITIL Staff: Select a group and load its agents
  const selectGroup = async (group) => {
    setSelectedGroup(group);
    setLoading(true);
    try {
      const data = await api.getAgentsByGroup(group.id);
      setAgentsInGroup(data.agents);
      setStep('agents');
    } catch (error) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  // ITIL Staff: Start chat with specific agent
  const startChatWithAgent = async (agent) => {
    setLoading(true);
    try {
      const response = await api.createChatSession({
        initial_message: initialMessage || `Chat request from ${user.full_name}`,
        preferred_agent_id: agent.user_id,
        preferred_group_id: selectedGroup?.id
      });
      
      if (response.success) {
        setSession(response.session);
        setQueuePosition(response.session.queue_position);
        const chatData = await api.getChatSession(response.session.id);
        setMessages(chatData.messages);
        setStep(response.session.status === 'active' ? 'active' : 'queue');
        toast.success('Chat session created!');
      } else {
        toast.info(response.message);
        if (response.session) {
          setSession(response.session);
          setStep(response.session.status);
        }
      }
    } catch (error) {
      toast.error('Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  // End User: Start chat (with availability check)
  const startChat = useCallback(async (message = null) => {
    setLoading(true);
    try {
      const response = await api.createSessionOrTicket({
        initial_message: message || initialMessage || 'I need help from a support agent'
      });
      
      if (response.success) {
        setSession(response.session);
        setQueuePosition(response.session.queue_position);
        const chatData = await api.getChatSession(response.session.id);
        setMessages(chatData.messages);
        setStep(response.session.status === 'active' ? 'active' : 'queue');
        toast.success('Chat session created!');
      } else if (response.offer_ticket) {
        // No agents available - show ticket option
        setInitialMessage(response.initial_message || message);
        setStep('no_agents');
      } else {
        toast.info(response.message);
        if (response.session) {
          setSession(response.session);
          setStep(response.session.status);
        }
      }
    } catch (error) {
      toast.error('Failed to start chat session');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user, initialMessage]);

  // Create ticket instead of chat
  const createTicketInstead = async () => {
    // Redirect to create incident with the message
    const ticketMessage = initialMessage || 'Support request from live chat';
    window.location.href = `/incidents/create?description=${encodeURIComponent(ticketMessage)}&source=live_chat`;
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!session || !user?.id) return;

    const connectWebSocket = () => {
      try {
        const wsUrl = api.getLiveChatWebSocketUrl(user.id);
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('User WebSocket connected');
          wsRef.current.send(JSON.stringify({ 
            type: 'join_chat', 
            session_id: session.id 
          }));
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_message' && data.message.session_id === session.id) {
            setMessages(prev => {
              if (prev.some(m => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
            setAgentTyping(false); // Clear typing when message received
          } else if (data.type === 'user_typing' && data.session_id === session.id) {
            // Agent is typing
            setAgentTyping(true);
            setTimeout(() => setAgentTyping(false), 3000);
          } else if (data.type === 'agent_joined') {
            setSession(prev => ({ ...prev, status: 'active', queue_position: 0 }));
            setQueuePosition(0);
            setStep('active');
            toast.success(`${data.agent_name} has joined the chat!`);
          } else if (data.type === 'chat_closed') {
            toast.info('Chat has been closed');
            setSession(prev => ({ ...prev, status: 'closed' }));
          } else if (data.type === 'chat_transferred') {
            toast.info(`Chat transferred to ${data.to_agent}`);
          }
        };

        wsRef.current.onclose = () => {
          setTimeout(connectWebSocket, 3000);
        };
      } catch (error) {
        console.error('WebSocket error:', error);
      }
    };

    connectWebSocket();

    pollRef.current = setInterval(async () => {
      try {
        const chatData = await api.getChatSession(session.id);
        setSession(chatData.session);
        setMessages(chatData.messages);
        setQueuePosition(chatData.session.queue_position);
        if (chatData.session.status === 'active' && step !== 'active') {
          setStep('active');
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 5000);

    return () => {
      wsRef.current?.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [session, user]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !session) return;

    setSending(true);
    try {
      await api.sendLiveChatMessage(session.id, {
        session_id: session.id,
        content: newMessage.trim(),
        sender_type: 'user'
      });
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Send typing indicator
  const sendTypingIndicator = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000 && session?.id) {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ 
          type: 'typing', 
          session_id: session.id,
          user_name: user?.full_name || 'User'
        }));
        lastTypingSentRef.current = now;
      }
    }
  };

  // Close chat
  const handleCloseChat = async () => {
    if (!session) return;
    try {
      await api.closeChat(session.id);
      setSession(null);
      setMessages([]);
      setStep('initial');
      toast.success('Chat closed');
    } catch (error) {
      toast.error('Failed to close chat');
    }
  };

  // When user leaves queue - offer chatback instead of closing immediately
  const handleLeaveQueue = () => {
    if (session && session.status === 'queued') {
      setStep('chatback_offer');
    } else {
      handleCloseChat();
    }
  };

  // User requests a chatback
  const handleRequestChatback = async () => {
    if (!session) return;
    try {
      // Close the current session first (marks as abandoned)
      await api.closeChat(session.id);
      // Request chatback
      const response = await api.post('/live-chat/chatback-request', {
        session_id: session.id,
        message_context: messages.filter(m => m.sender_type === 'user').map(m => m.content).join(' | ').slice(0, 500)
      });
      if (response.data.success) {
        setStep('chatback_confirmed');
        toast.success('Chat-back request created!');
      }
    } catch (error) {
      toast.error('Failed to request chat-back');
      handleCloseChat();
    }
  };

  // User declines chatback - just close
  const handleDeclineChatback = async () => {
    await handleCloseChat();
  };

  // Format time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="live-chat-widget">
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-md h-[600px] flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {(onBackToAmie || step === 'agents') && (
              <Button variant="ghost" size="sm" onClick={() => {
                if (step === 'agents') {
                  setStep('groups');
                  setSelectedGroup(null);
                } else if (onBackToAmie) {
                  onBackToAmie();
                }
              }}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <Headphones className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Live Support</h3>
              <p className="text-xs text-muted-foreground">
                {step === 'groups' && 'Select a team'}
                {step === 'agents' && selectedGroup?.name}
                {step === 'no_agents' && 'No agents available'}
                {step === 'queue' && `#${queuePosition} in queue`}
                {step === 'active' && `Connected with ${session?.agent_name}`}
                {step === 'initial' && 'Connect with an agent'}
                {step === 'chatback_offer' && 'Chat-Back Option'}
                {step === 'chatback_confirmed' && 'Chat-Back Requested'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : step === 'groups' ? (
            // ITIL Staff: Group Selection
            <ScrollArea className="h-full p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground mb-4">
                  Select a team to chat with:
                </p>
                {groups.map((group) => (
                  <Card 
                    key={group.id}
                    className={`p-3 cursor-pointer hover:bg-muted transition-colors ${
                      !group.has_available_agents ? 'opacity-50' : ''
                    }`}
                    onClick={() => group.has_available_agents && selectGroup(group)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{group.name}</p>
                          {group.description && (
                            <p className="text-xs text-muted-foreground">{group.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={group.online_agents > 0 ? 'default' : 'secondary'}>
                          {group.online_agents} online
                        </Badge>
                        {group.has_available_agents && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
                {groups.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No teams available</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : step === 'agents' ? (
            // ITIL Staff: Agent Selection
            <ScrollArea className="h-full p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground mb-4">
                  Select an agent from {selectedGroup?.name}:
                </p>
                {agentsInGroup.map((agent) => (
                  <Card 
                    key={agent.user_id}
                    className={`p-3 cursor-pointer hover:bg-muted transition-colors ${
                      agent.is_at_capacity ? 'opacity-50' : ''
                    }`}
                    onClick={() => !agent.is_at_capacity && startChatWithAgent(agent)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                          <UserCheck className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{agent.user_name}</p>
                          <p className="text-xs text-muted-foreground">{agent.user_email}</p>
                        </div>
                      </div>
                      <Badge variant={agent.is_at_capacity ? 'destructive' : 'default'}>
                        {agent.available_slots} slots
                      </Badge>
                    </div>
                  </Card>
                ))}
                {agentsInGroup.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <UserCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No agents online in this team</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : step === 'no_agents' ? (
            // No Agents Available - Offer Ticket
            <div className="h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Agents Available</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Our support agents are currently busy or offline. Would you like to create a support ticket instead?
              </p>
              <div className="space-y-3 w-full">
                <Button 
                  onClick={createTicketInstead}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <Ticket className="h-4 w-4 mr-2" />
                  Create Support Ticket
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => initializeFlow()}
                  className="w-full"
                >
                  Check Again
                </Button>
                <p className="text-xs text-muted-foreground">
                  Our team will get back to you as soon as possible
                </p>
              </div>
            </div>
          ) : step === 'initial' ? (
            // Start Chat View (End Users)
            <div className="h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Chat with Support</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Connect with our support team for real-time assistance
              </p>
              <Input
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                placeholder="Briefly describe your issue (optional)"
                className="mb-4"
              />
              <Button 
                onClick={() => startChat(initialMessage)}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Headphones className="h-4 w-4 mr-2" />
                )}
                Start Chat
              </Button>
            </div>
          ) : step === 'queue' ? (
            // Queue View
            <div className="h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="relative mb-6">
                <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center">
                  <Clock className="h-10 w-10 text-orange-600 animate-pulse" />
                </div>
                <Badge className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-orange-600">
                  #{queuePosition}
                </Badge>
              </div>
              <h3 className="text-lg font-semibold mb-2">You're in Queue</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Position: <strong>#{queuePosition}</strong>
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                An agent will be with you shortly. Please wait...
              </p>
              
              {messages.length > 0 && (
                <ScrollArea className="w-full h-32 border rounded-lg p-2 mb-4">
                  {messages.map((msg) => (
                    <div key={msg.id} className="text-sm text-left mb-2">
                      <span className="font-medium">{msg.sender_name}: </span>
                      {msg.sender_type === 'agent' && msg.translated_content ? msg.translated_content : msg.content}
                    </div>
                  ))}
                </ScrollArea>
              )}
              
              <Button variant="outline" onClick={handleLeaveQueue}>
                Cancel
              </Button>
            </div>
          ) : step === 'chatback_offer' ? (
            // Chat-Back Offer View
            <div className="h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="h-16 w-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Would you like us to reach back?</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Our agents are currently unavailable. We can add you to our chat-back queue and an agent will start a new chat with you as soon as one becomes available.
              </p>
              <div className="space-y-3 w-full">
                <Button 
                  onClick={handleRequestChatback}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  data-testid="chatback-request-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Yes, Chat Me Back
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleDeclineChatback}
                  className="w-full"
                  data-testid="chatback-decline-btn"
                >
                  No Thanks
                </Button>
                <p className="text-xs text-muted-foreground">
                  A linked incident will be auto-created for tracking
                </p>
              </div>
            </div>
          ) : step === 'chatback_confirmed' ? (
            // Chat-Back Confirmed View
            <div className="h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Chat-Back Requested!</h3>
              <p className="text-sm text-muted-foreground mb-6">
                You're in the chat-back queue. An agent will start a new chat with you soon. You'll see a notification when they're ready.
              </p>
              <Button 
                variant="outline" 
                onClick={() => { setSession(null); setMessages([]); setStep('initial'); onClose(); }}
                className="w-full"
              >
                Close
              </Button>
            </div>
          ) : (
            // Active Chat View
            <ScrollArea className="h-full p-4">
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.sender_type === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : msg.sender_type === 'amie'
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100'
                          : msg.sender_type === 'system'
                          ? 'bg-muted text-muted-foreground text-center text-sm italic w-full'
                          : 'bg-muted'
                      }`}
                    >
                      {msg.sender_type !== 'system' && msg.sender_type !== 'user' && (
                        <div className="flex items-center gap-2 mb-1">
                          {msg.sender_type === 'amie' ? (
                            <Bot className="h-3 w-3" />
                          ) : (
                            <User className="h-3 w-3" />
                          )}
                          <span className="text-xs font-medium">{msg.sender_name}</span>
                        </div>
                      )}
                      {/* Show translated content for agent messages if available */}
                      {msg.sender_type === 'agent' && msg.translated_content ? (
                        <p className="text-sm whitespace-pre-wrap">{msg.translated_content}</p>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      )}
                      <p className="text-xs opacity-70 mt-1">{formatDateTime(msg.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Message Input (only show when active) */}
        {step === 'active' && (
          <div className="border-t p-4">
            {/* Typing Indicator */}
            {agentTyping && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2 animate-pulse">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                </div>
                <span>Agent is typing...</span>
              </div>
            )}
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  sendTypingIndicator();
                }}
                placeholder="Type your message..."
                disabled={sending}
                data-testid="user-chat-input"
              />
              <Button type="submit" disabled={sending || !newMessage.trim()}>
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Close button for active chats */}
        {step === 'active' && (
          <div className="border-t p-2 flex justify-center">
            <Button variant="ghost" size="sm" onClick={handleCloseChat} className="text-muted-foreground">
              End Chat
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
